import React, { useEffect, useState, useCallback } from "react";
import "./App.css";
import Household from "./Household";

const API = process.env.REACT_APP_API_URL ?? "";

type Page = "dashboard" | "journal" | "household";
type ViewMode = "day" | "week" | "all";

interface Decision {
  id: string;
  description: string;
  category: "parent" | "child";
  happened_at: string;
  activity: string | null;
}

interface RangeStats {
  parent_count: number;
  child_count: number;
  total: number;
  parent_ratio: number;
  child_ratio: number;
  decisions: Decision[];
}

interface Streaks {
  current_streak: number;
  current_streak_type: string;
  longest_streak: number;
  longest_streak_type: string;
}

interface ActivityHeatmap {
  activity: string;
  category: "parent" | "child";
  days: Record<string, number>;
}

interface JournalEntry {
  id: string;
  content: string;
  created_at: string;
}

interface MoodEntry {
  id: string;
  rating: number;
  feelings: string;
  created_at: string;
}

interface WeeklySummary {
  week_start: string;
  summary: string | null;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfWeek(d: Date): Date {
  const day = startOfDay(d);
  const dow = day.getDay();
  day.setDate(day.getDate() - ((dow + 6) % 7));
  return day;
}

function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(d: Date): string {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatDateRange(start: Date, mode: ViewMode): string {
  if (mode === "day") {
    const today = startOfDay(new Date());
    const diff = Math.round((today.getTime() - start.getTime()) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return formatDateLabel(start);
  }
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${formatDateLabel(start)} – ${formatDateLabel(end)}`;
}

interface WeekColumn {
  weekStart: string;
  days: { date: string; dow: number }[];
}

function buildWeekColumns(numWeeks: number = 13): WeekColumn[] {
  const today = startOfDay(new Date());
  const todayDow = today.getDay();
  const currentSunday = new Date(today);
  currentSunday.setDate(currentSunday.getDate() - todayDow);

  const columns: WeekColumn[] = [];
  for (let w = numWeeks - 1; w >= 0; w--) {
    const sunday = new Date(currentSunday);
    sunday.setDate(sunday.getDate() - w * 7);
    const days: { date: string; dow: number }[] = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(sunday);
      day.setDate(day.getDate() + d);
      if (day <= today) {
        days.push({ date: fmtDate(day), dow: d });
      }
    }
    if (days.length > 0) {
      columns.push({ weekStart: fmtDate(sunday), days });
    }
  }
  return columns;
}

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

function getMonthLabels(columns: WeekColumn[]): { label: string; colIndex: number }[] {
  const labels: { label: string; colIndex: number }[] = [];
  let lastMonth = "";
  columns.forEach((col, i) => {
    const d = new Date(col.weekStart + "T00:00:00");
    const month = d.toLocaleDateString("en-US", { month: "short" });
    if (month !== lastMonth) {
      labels.push({ label: month, colIndex: i });
      lastMonth = month;
    }
  });
  return labels;
}

function ActivityGrid({ activity, category, days }: ActivityHeatmap) {
  const columns = buildWeekColumns(13);
  const baseColor = category === "parent" ? [74, 222, 128] : [248, 113, 113];
  const monthLabels = getMonthLabels(columns);

  return (
    <div className="activity-grid">
      <div className="activity-label">
        <span className="activity-name">{activity}</span>
      </div>
      <div className="github-grid">
        <div className="grid-day-labels">
          {DAY_LABELS.map((label, i) => (
            <div key={i} className="grid-day-label">{i % 2 === 1 ? label : ""}</div>
          ))}
        </div>
        <div className="grid-main">
          <div className="grid-month-labels">
            {monthLabels.map((m, i) => (
              <div key={i} className="grid-month-label" style={{ gridColumnStart: m.colIndex + 1 }}>{m.label}</div>
            ))}
          </div>
          <div className="grid-weeks">
            {columns.map((col) => (
              <div key={col.weekStart} className="grid-week">
                {Array.from({ length: 7 }, (_, dow) => {
                  const dayEntry = col.days.find((d) => d.dow === dow);
                  if (!dayEntry) return <div key={dow} className="grid-cell empty" />;
                  const count = days[dayEntry.date] || 0;
                  const opacity = count === 0 ? 0 : Math.min(0.3 + count * 0.25, 1);
                  const tooltipText = count === 0 ? `${dayEntry.date}: none` : `${dayEntry.date}: ${count} time${count !== 1 ? "s" : ""}`;
                  return (
                    <div key={dow} className="grid-cell-wrap" data-tooltip={tooltipText}>
                      <div className="grid-cell" style={{
                        backgroundColor: count === 0 ? "rgba(255,255,255,0.06)" : `rgba(${baseColor.join(",")},${opacity})`,
                      }} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CollapsibleSection({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="collapsible-section">
      <button className="collapsible-header" onClick={() => setOpen(!open)}>
        <h2 className="section-header">{title}</h2>
        <span className={`collapse-arrow ${open ? "open" : ""}`}>▸</span>
      </button>
      {open && children}
    </div>
  );
}

const FEELING_PALETTE = [
  "#f472b6", "#fb923c", "#a78bfa", "#38bdf8", "#4ade80",
  "#facc15", "#f87171", "#34d399", "#c084fc", "#fb7185",
  "#22d3ee", "#a3e635", "#fbbf24", "#818cf8", "#e879f9",
  "#2dd4bf", "#f97316", "#67e8f9", "#d946ef", "#84cc16",
];

function getFeelingColorMap(entries: MoodEntry[]): Record<string, string> {
  const allFeelings = new Set<string>();
  entries.forEach((e) => {
    if (e.feelings) {
      e.feelings.split(",").map((f) => f.trim().toLowerCase()).filter(Boolean).forEach((f) => allFeelings.add(f));
    }
  });
  const map: Record<string, string> = {};
  let i = 0;
  allFeelings.forEach((f) => {
    map[f] = FEELING_PALETTE[i % FEELING_PALETTE.length];
    i++;
  });
  return map;
}

function MoodChart({ entries }: { entries: MoodEntry[] }) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const feelingColors = getFeelingColorMap(entries);
  if (entries.length === 0) return null;

  const sorted = [...entries].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const svgWidth = 600;
  const svgHeight = 220;
  const padding = { top: 20, right: 60, bottom: 45, left: 45 };
  const chartW = svgWidth - padding.left - padding.right;
  const chartH = svgHeight - padding.top - padding.bottom;

  const minTime = new Date(sorted[0].created_at).getTime();
  const maxTime = new Date(sorted[sorted.length - 1].created_at).getTime();
  const timeSpan = maxTime - minTime;

  const points = sorted.map((e) => {
    const t = new Date(e.created_at).getTime();
    const x = timeSpan === 0 ? chartW / 2 : ((t - minTime) / timeSpan) * chartW;
    const y = chartH - ((e.rating - 1) / 9) * chartH;
    return { x: x + padding.left, y: y + padding.top, entry: e };
  });

  const midY = padding.top + chartH - ((5 - 1) / 9) * chartH;
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const areaPath = linePath + ` L${points[points.length - 1].x},${padding.top + chartH} L${points[0].x},${padding.top + chartH} Z`;

  return (
    <div className="mood-chart-section">
      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="mood-chart-svg">
        <defs>
          <clipPath id="clipAbove"><rect x={padding.left} y={padding.top} width={chartW} height={midY - padding.top} /></clipPath>
          <clipPath id="clipBelow"><rect x={padding.left} y={midY} width={chartW} height={padding.top + chartH - midY} /></clipPath>
          <linearGradient id="greenGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4ade80" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#4ade80" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="redGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f87171" stopOpacity="0.02" />
            <stop offset="100%" stopColor="#f87171" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {[1, 3, 5, 7, 10].map((v) => {
          const y = padding.top + chartH - ((v - 1) / 9) * chartH;
          return (
            <g key={v}>
              <line x1={padding.left} y1={y} x2={svgWidth - padding.right} y2={y} stroke={v === 5 ? "#444" : "#2a2a2a"} strokeWidth="1" strokeDasharray={v === 5 ? "4 3" : "none"} />
              <text x={padding.left - 8} y={y + 4} fill="#555" fontSize="10" textAnchor="end">{v}</text>
            </g>
          );
        })}
        {points.map((p, i) => {
          const d = new Date(p.entry.created_at);
          const label = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          const showDate = sorted.length > 1 && (i === 0 || new Date(sorted[i - 1].created_at).toDateString() !== d.toDateString());
          const skip = points.length > 8 ? Math.ceil(points.length / 6) : 1;
          if (i % skip !== 0 && i !== points.length - 1) return null;
          return (
            <g key={`t${i}`}>
              {showDate && <text x={p.x} y={padding.top + chartH + 14} fill="#666" fontSize="9" textAnchor="middle">{d.toLocaleDateString([], { month: "short", day: "numeric" })}</text>}
              <text x={p.x} y={padding.top + chartH + (showDate ? 26 : 16)} fill="#555" fontSize="9" textAnchor="middle">{label}</text>
            </g>
          );
        })}
        <path d={areaPath} fill="url(#greenGrad)" clipPath="url(#clipAbove)" />
        <path d={areaPath} fill="url(#redGrad)" clipPath="url(#clipBelow)" />
        <path d={linePath} fill="none" stroke="#4ade80" strokeWidth="2" strokeLinejoin="round" clipPath="url(#clipAbove)" />
        <path d={linePath} fill="none" stroke="#f87171" strokeWidth="2" strokeLinejoin="round" clipPath="url(#clipBelow)" />
        {points.map((p, i) => {
          const color = p.entry.rating >= 5 ? "#4ade80" : "#f87171";
          const feelings = p.entry.feelings
            ? p.entry.feelings.split(",").map((f) => f.trim()).filter(Boolean)
            : [];
          const above = p.y > midY;
          const labelY = above ? p.y - 12 : p.y + 16;
          const shown = feelings.slice(0, 2);
          const extra = feelings.length - shown.length;
          const labelText = shown.length > 0
            ? shown.join(", ") + (extra > 0 ? ` +${extra}` : "")
            : "";
          const isHovered = hoveredIdx === i;
          const tooltipAbove = p.y > midY;
          const tooltipY = tooltipAbove ? p.y - 20 : p.y + 24;
          const allFeelings = feelings.join(", ");
          const timeLabel = new Date(p.entry.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
          return (
            <g key={i}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ cursor: "pointer" }}
            >
              <circle cx={p.x} cy={p.y} r="8" fill="transparent" />
              <circle cx={p.x} cy={p.y} r={isHovered ? 6 : 5} fill={color} stroke="#0f0f0f" strokeWidth="2" />
              {shown.length > 0 && !isHovered && (
                <>
                  <text x={p.x} y={labelY} fontSize="8" textAnchor="middle" stroke="#0f0f0f" strokeWidth="3" paintOrder="stroke">
                    {shown.map((f, fi) => (
                      <tspan key={fi} fill={feelingColors[f.toLowerCase()] || color}>
                        {fi > 0 ? ", " : ""}{f}
                      </tspan>
                    ))}
                    {extra > 0 && <tspan fill="#888"> +{extra}</tspan>}
                  </text>
                  <text x={p.x} y={labelY} fontSize="8" textAnchor="middle">
                    {shown.map((f, fi) => (
                      <tspan key={fi} fill={feelingColors[f.toLowerCase()] || color}>
                        {fi > 0 ? ", " : ""}{f}
                      </tspan>
                    ))}
                    {extra > 0 && <tspan fill="#888"> +{extra}</tspan>}
                  </text>
                </>
              )}
              {isHovered && (
                <g>
                  <rect
                    x={p.x - 75} y={tooltipAbove ? tooltipY - 30 : tooltipY - 4}
                    width="150" height={feelings.length > 0 ? 34 : 20}
                    rx="6" fill="#1a1a1a" stroke="#333" strokeWidth="1"
                  />
                  <text x={p.x} y={tooltipAbove ? tooltipY - 16 : tooltipY + 10} fill="#eee" fontSize="9" textAnchor="middle" fontWeight="600">
                    {p.entry.rating}/10 · {timeLabel}
                  </text>
                  {feelings.length > 0 && (
                    <text x={p.x} y={tooltipAbove ? tooltipY - 4 : tooltipY + 22} fontSize="8" textAnchor="middle">
                      {feelings.map((f, fi) => (
                        <tspan key={fi} fill={feelingColors[f.toLowerCase()] || color}>
                          {fi > 0 ? ", " : ""}{f}
                        </tspan>
                      ))}
                    </text>
                  )}
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function FeelingsList({ entries }: { entries: MoodEntry[] }) {
  if (entries.length === 0) return null;

  const feelingColors = getFeelingColorMap(entries);

  const allFeelings = entries
    .filter((e) => e.feelings)
    .flatMap((e) => e.feelings.split(",").map((f) => f.trim().toLowerCase()))
    .filter(Boolean);

  const counts: Record<string, number> = {};
  allFeelings.forEach((f) => { counts[f] = (counts[f] || 0) + 1; });

  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  return (
    <div className="feelings-log">
      {sorted.length > 0 && (
        <div className="feelings-pills">
          {sorted.map(([feeling, count]) => (
            <span
              key={feeling}
              className="feeling-pill"
              style={{ color: feelingColors[feeling], backgroundColor: `${feelingColors[feeling]}18` }}
            >
              {feeling}{count > 1 ? ` · ${count}` : ""}
            </span>
          ))}
        </div>
      )}
      <div className="mood-entries-list">
        {entries.map((e) => (
          <div key={e.id} className="mood-entry">
            <span className="mood-rating-badge" data-level={e.rating >= 7 ? "high" : e.rating >= 4 ? "mid" : "low"}>
              {e.rating}
            </span>
            <span className="mood-entry-feelings">{e.feelings || "—"}</span>
            <span className="mood-entry-time">
              {new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const [mode, setMode] = useState<ViewMode>("day");
  const [offset, setOffset] = useState(0);
  const [stats, setStats] = useState<RangeStats | null>(null);
  const [streaks, setStreaks] = useState<Streaks | null>(null);
  const [heatmaps, setHeatmaps] = useState<ActivityHeatmap[]>([]);
  const [moodEntries, setMoodEntries] = useState<MoodEntry[]>([]);

  const [logLimit, setLogLimit] = useState(20);

  const getRange = useCallback((): [string, string] | null => {
    if (mode === "all") return null;
    const now = new Date();
    let start: Date, end: Date;
    if (mode === "day") {
      start = startOfDay(now);
      start.setDate(start.getDate() - offset);
      end = new Date(start);
      end.setDate(end.getDate() + 1);
    } else {
      start = startOfWeek(now);
      start.setDate(start.getDate() - offset * 7);
      end = new Date(start);
      end.setDate(end.getDate() + 7);
    }
    return [fmtDate(start), fmtDate(end)];
  }, [mode, offset]);

  const getRangeStart = useCallback((): Date => {
    const now = new Date();
    if (mode === "day") {
      const d = startOfDay(now);
      d.setDate(d.getDate() - offset);
      return d;
    }
    const d = startOfWeek(now);
    d.setDate(d.getDate() - offset * 7);
    return d;
  }, [mode, offset]);

  useEffect(() => {
    setLogLimit(20);
    const range = getRange();
    const tzOffset = new Date().getTimezoneOffset();
    const rangeParams = range ? `start=${range[0]}&end=${range[1]}&tz_offset=${tzOffset}` : `tz_offset=${tzOffset}`;
    const decisionUrl = range
      ? `${API}/api/decisions/range?start=${range[0]}&end=${range[1]}&tz_offset=${tzOffset}`
      : `${API}/api/stats/all`;
    Promise.all([
      fetch(decisionUrl).then((r) => r.json()),
      fetch(`${API}/api/streaks`).then((r) => r.json()),
      fetch(`${API}/api/heatmap?days=${mode === "all" ? 3650 : 90}&tz_offset=${tzOffset}`).then((r) => r.json()),
      fetch(`${API}/api/mood?${rangeParams}`).then((r) => r.json()),
    ]).then(([s, st, h, m]) => {
      setStats(s);
      setStreaks(st);
      setHeatmaps(h);
      setMoodEntries(m);
    });
  }, [getRange, mode]);

  const allDecisions = stats?.decisions ?? [];
  const visibleDecisions = mode === "all" ? allDecisions.slice(0, logLimit) : allDecisions;
  const hasMore = mode === "all" && logLimit < allDecisions.length;

  const groupedByDate = visibleDecisions.reduce<Record<string, Decision[]>>((acc, d) => {
    const day = new Date(d.happened_at).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
    if (!acc[day]) acc[day] = [];
    acc[day].push(d);
    return acc;
  }, {});

  const activityCounts = (stats?.decisions ?? []).reduce<Record<string, { count: number; category: string }>>((acc, d) => {
    const key = d.activity || d.description;
    if (!acc[key]) acc[key] = { count: 0, category: d.category };
    acc[key].count++;
    return acc;
  }, {});

  const parentActivities = Object.entries(activityCounts).filter(([, v]) => v.category === "parent");
  const childActivities = Object.entries(activityCounts).filter(([, v]) => v.category === "child");
  const isToday = offset === 0;

  return (
    <>
      <div className="mode-selector-row">
        <div className="mode-selector">
          {(["day", "week", "all"] as ViewMode[]).map((m) => (
            <button key={m} className={mode === m ? "active" : ""} onClick={() => { setMode(m); setOffset(0); }}>
              {m === "day" ? "Daily" : m === "week" ? "Weekly" : "All Time"}
            </button>
          ))}
        </div>
      </div>

      {mode !== "all" && (
        <div className="date-nav">
          <button onClick={() => setOffset(offset + 1)}>←</button>
          <span className="date-label">{formatDateRange(getRangeStart(), mode)}</span>
          <button onClick={() => setOffset(Math.max(0, offset - 1))} disabled={isToday}>→</button>
        </div>
      )}

      <CollapsibleSection title="Mood">
        <MoodChart entries={moodEntries} />
        <FeelingsList entries={moodEntries} />
      </CollapsibleSection>

      <CollapsibleSection title="Parent / Child">
        <div className="summary-strip">
          <div className="summary-counts">
            <div className="summary-count parent">
              <span className="count-number">{stats?.parent_count ?? 0}</span>
              <span className="count-label">Parent</span>
            </div>
            <div className="summary-divider" />
            <div className="summary-count child">
              <span className="count-number">{stats?.child_count ?? 0}</span>
              <span className="count-label">Child</span>
            </div>
            {mode === "all" && streaks && streaks.current_streak > 1 && (
              <>
                <div className="summary-divider" />
                <div className="summary-count streak">
                  <span className="count-number">{streaks.current_streak}</span>
                  <span className="count-label">{streaks.current_streak_type} streak</span>
                </div>
              </>
            )}
          </div>
          {stats && stats.total > 0 && (
            <div className="ratio-bar">
              <div className="ratio-fill parent" style={{ width: `${stats.parent_ratio * 100}%` }} />
              <div className="ratio-fill child" style={{ width: `${stats.child_ratio * 100}%` }} />
            </div>
          )}
          {(parentActivities.length > 0 || childActivities.length > 0) && (
            <div className="activity-pills">
              {parentActivities.map(([name, { count }]) => (
                <span key={name} className="activity-pill parent">{name} · {count}</span>
              ))}
              {childActivities.map(([name, { count }]) => (
                <span key={name} className="activity-pill child">{name} · {count}</span>
              ))}
            </div>
          )}
        </div>
      </CollapsibleSection>

      {heatmaps.length > 0 && (
        <CollapsibleSection title="Activities">
          <div className="heatmap-section">
            <div className="heatmap-columns">
              <div className="heatmap-col">
                <h3 className="heatmap-col-title child">Child</h3>
                {heatmaps.filter((h) => h.category === "child").map((h) => (
                  <ActivityGrid key={h.activity} {...h} />
                ))}
              </div>
              <div className="heatmap-col">
                <h3 className="heatmap-col-title parent">Parent</h3>
                {heatmaps.filter((h) => h.category === "parent").map((h) => (
                  <ActivityGrid key={h.activity} {...h} />
                ))}
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}

      <div className="log-section">
        <h2 className="log-header">Log</h2>
        {Object.keys(groupedByDate).length === 0 ? (
          <div className="empty-state">No decisions logged{mode !== "all" ? ` for this ${mode}` : ""}.</div>
        ) : (
          <>
            {Object.entries(groupedByDate).map(([date, decisions]) => (
              <div key={date} className="day-group">
                <div className="day-header">{date}</div>
                {decisions.map((d) => (
                  <div key={d.id} className={`decision ${d.category}`}>
                    <span className="decision-emoji">{d.category === "parent" ? "🧠" : "🍬"}</span>
                    <span className="decision-text">
                      {d.activity && <span className="activity-badge">{d.activity}</span>}
                      {d.description}
                    </span>
                    <span className="decision-time">
                      {new Date(d.happened_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                ))}
              </div>
            ))}
            {hasMore && (
              <button className="show-more-btn" onClick={() => setLogLimit(logLimit + 20)}>
                Show more ({allDecisions.length - logLimit} remaining)
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Journal() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [summary, setSummary] = useState<WeeklySummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const getWeekRange = useCallback((): [string, string, string] => {
    const now = new Date();
    const ws = startOfWeek(now);
    ws.setDate(ws.getDate() - weekOffset * 7);
    const we = new Date(ws);
    we.setDate(we.getDate() + 7);
    return [fmtDate(ws), fmtDate(we), fmtDate(ws)];
  }, [weekOffset]);

  useEffect(() => {
    const [start, end, weekStart] = getWeekRange();
    const tzOffset = new Date().getTimezoneOffset();

    fetch(`${API}/api/journal?start=${start}&end=${end}&tz_offset=${tzOffset}`)
      .then((r) => r.json())
      .then(setEntries);

    fetchSummary(weekStart, tzOffset, false);
  }, [getWeekRange]);

  const fetchSummary = (weekStart: string, tzOffset: number, force: boolean) => {
    setLoadingSummary(true);
    fetch(`${API}/api/weekly-summary?week_start=${weekStart}&tz_offset=${tzOffset}&force=${force}`)
      .then((r) => r.json())
      .then((s) => { setSummary(s); setLoadingSummary(false); })
      .catch(() => setLoadingSummary(false));
  };

  const regenerateSummary = () => {
    const [,, weekStart] = getWeekRange();
    fetchSummary(weekStart, new Date().getTimezoneOffset(), true);
  };

  const weekStart = (() => {
    const now = new Date();
    const ws = startOfWeek(now);
    ws.setDate(ws.getDate() - weekOffset * 7);
    return ws;
  })();

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const groupedByDate = entries.reduce<Record<string, JournalEntry[]>>((acc, e) => {
    const day = new Date(e.created_at).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
    if (!acc[day]) acc[day] = [];
    acc[day].push(e);
    return acc;
  }, {});

  return (
    <>
      <div className="date-nav">
        <button onClick={() => setWeekOffset(weekOffset + 1)}>←</button>
        <span className="date-label">
          {formatDateLabel(weekStart)} – {formatDateLabel(weekEnd)}
        </span>
        <button onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))} disabled={weekOffset === 0}>→</button>
      </div>

      {(summary?.summary || loadingSummary) && (
        <div className="weekly-summary">
          <div className="summary-header">
            <h3>Weekly Summary</h3>
            <button className="regenerate-btn" onClick={regenerateSummary} disabled={loadingSummary}>
              {loadingSummary ? "..." : "↻"}
            </button>
          </div>
          {loadingSummary ? (
            <p className="summary-loading">Generating summary...</p>
          ) : (
            <p>{summary!.summary}</p>
          )}
        </div>
      )}

      <div className="journal-section">
        {Object.keys(groupedByDate).length === 0 ? (
          <div className="empty-state">No journal entries this week.</div>
        ) : (
          Object.entries(groupedByDate).map(([date, dayEntries]) => (
            <div key={date} className="day-group">
              <div className="day-header">{date}</div>
              {dayEntries.map((e) => (
                <div key={e.id} className="journal-entry">
                  <span className="journal-time">
                    {new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span className="journal-content">{e.content}</span>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </>
  );
}

function App() {
  const householdOnly = document.documentElement.dataset.householdLocal === "true";
  const [page, setPage] = useState<Page>(householdOnly || window.location.hash === "#household" ? "household" : "dashboard");

  return (
    <div className="app">
      <header>
        <h1>{householdOnly ? "Household" : "Accountability"}</h1>
        <nav className="page-nav">
          {!householdOnly && <button className={page === "dashboard" ? "active" : ""} onClick={() => setPage("dashboard")}>Dashboard</button>}
          {!householdOnly && <button className={page === "journal" ? "active" : ""} onClick={() => setPage("journal")}>Journal</button>}
          <button className={page === "household" ? "active" : ""} onClick={() => { setPage("household"); window.location.hash = "household"; }}>Household</button>
        </nav>
      </header>
      {page === "dashboard" ? <Dashboard /> : page === "journal" ? <Journal /> : <Household />}
    </div>
  );
}

export default App;
